import { useEffect, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { SupplierDeliveryScheduleEditor } from "@/components/suppliers/SupplierDeliveryScheduleEditor";
import { api } from "@/lib/apiClient";
import type { SupplierDeliverySchedule } from "@/types/camping";

const EMPTY_FORM = {
  name: "",
  contact_person: "",
  email: "",
  phone: "",
  address: "",
  notes: "",
  orders_enabled: false,
  delivery_schedules: [] as SupplierDeliverySchedule[],
};

export interface CreateSupplierModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

export function CreateSupplierModal({ open, onClose, onCreated }: CreateSupplierModalProps) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY_FORM);
    setError(null);
    setSubmitting(false);
  }, [open]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.post("/api/v1/suppliers", {
        name: form.name.trim(),
        contact_person: form.contact_person.trim() || null,
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        address: form.address.trim() || null,
        notes: form.notes.trim() || null,
        orders_enabled: form.orders_enabled,
        delivery_schedules: form.delivery_schedules,
      });
      onCreated();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Fehler beim Anlegen");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Neuer Lieferant"
      description="Lieferantenstamm anlegen"
    >
      <form onSubmit={(e) => void onSubmit(e)} className="space-y-4">
        <label className="flex flex-col gap-1 text-sm">
          Name
          <input
            required
            autoFocus
            className="eh-input"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          Ansprechpartner
          <input
            className="eh-input"
            value={form.contact_person}
            onChange={(e) => setForm((f) => ({ ...f, contact_person: e.target.value }))}
          />
        </label>
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="flex flex-col gap-1 text-sm">
            E-Mail
            <input
              type="email"
              className="eh-input"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            Telefon
            <input
              className="eh-input"
              value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            />
          </label>
        </div>
        <label className="flex flex-col gap-1 text-sm">
          Adresse
          <textarea
            rows={2}
            className="eh-input"
            value={form.address}
            onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          Notizen
          <textarea
            rows={2}
            className="eh-input"
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
          />
        </label>

        <SupplierDeliveryScheduleEditor
          enabled={form.orders_enabled}
          schedules={form.delivery_schedules}
          onEnabledChange={(orders_enabled) => setForm((f) => ({ ...f, orders_enabled }))}
          onSchedulesChange={(delivery_schedules) =>
            setForm((f) => ({ ...f, delivery_schedules }))
          }
        />

        {error ? <p className="text-sm text-status-red">{error}</p> : null}
        <div className="eh-modal-actions flex flex-wrap justify-end gap-2 border-t border-separator pt-4">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Abbrechen
          </Button>
          <Button type="submit" loading={submitting}>
            Anlegen
          </Button>
        </div>
      </form>
    </Modal>
  );
}
